import { forwardRef, useCallback, useEffect, useRef } from 'react';
import { Close } from '@gjensidige/builders-icons';
import type { TrackingProp } from '../types';
import { useTracking } from '../utils';
import { Button } from '../';
import styles from './modal.module.css';
import clsx from 'clsx';

const DISABLE_ESC = 'dangerouslyDisableEscape';

export type ModalProps = Omit<
  React.ComponentPropsWithoutRef<'div'>,
  'aria-label'
> & {
  'aria-label': string;
  backdropProps?: React.ComponentPropsWithoutRef<'dialog'>;
  closeLabel?: string;
  onClose?: () => void;
  open?: boolean;
  size?: 'sm' | 'lg';
  strict?: boolean | typeof DISABLE_ESC;
  tracking?: TrackingProp;
  variant?: 'box' | 'box-outerscroll' | 'drawer';
};

const FOCUSABLE = `[tabindex],[contenteditable],:is(select,textarea,input,button):enabled:not(.${styles.close})`;
let IS_BACKDROP_DOWN = false;

export const ModalComp = forwardRef<HTMLDivElement, ModalProps>(function Modal(
  {
    backdropProps,
    children,
    className,
    closeLabel,
    onClose,
    open = false,
    size = 'lg',
    strict = false,
    tracking = true,
    variant = 'box',
    ...rest
  },
  ref
) {
  const backdropClasses = clsx(styles.backdrop, backdropProps?.className);
  const backdropRef = useRef<HTMLDialogElement>(null);
  const classes = clsx(styles.dialog, styles[variant], styles[size], className);

  const track = useTracking(tracking);
  const toggle = useCallback(
    (open: boolean, closeSuffix = '_dismiss') => {
      const dialog = backdropRef.current;
      const isNative = typeof dialog?.close === 'function';
      const focusable =
        dialog?.querySelector<HTMLElement>('[autofocus]') || // Split in 3 calls to prioritize autofocus
        dialog?.querySelector<HTMLElement>(FOCUSABLE) || // Then prioritize interactive elements except close button
        dialog?.querySelector<HTMLElement>(`.${styles.close}`); // Fallback to close button

      if (!dialog || dialog.hidden !== open) return; // Do not track if not actually changes to open
      if (isNative) dialog[open ? 'showModal' : 'close']();
      else if (!open) dialog.dispatchEvent(new CustomEvent('close')); // Support Safari 15

      dialog.hidden = !open; // Used to support Safari 15 that does not support <dialog> natively
      if (open) setTimeout(() => focusable?.focus()); // Move focus to dialog
      track({
        eventCategory: `modal_${variant}`,
        eventAction: open ? 'open' : `close${closeSuffix}`,
        eventLabel: dialog?.getAttribute('aria-label') || 'modal'
      });
    },
    [variant, track]
  );

  useEffect(() => toggle(open, ''), [open, toggle]); // Toggle each time open prop changes

  return (
    // Paint backdrop with div so Safari 15 gets backdrop and so we can make the backdrop scrollable
    <dialog
      {...backdropProps}
      className={backdropClasses}
      hidden
      ref={backdropRef}
      onPointerDown={(event) => {
        IS_BACKDROP_DOWN = event.target === backdropRef.current; // Only close if pressing down and up on the backdrop element itself
        backdropProps?.onPointerDown?.(event);
      }}
      onPointerUp={(event) => {
        const isBackdropDown = IS_BACKDROP_DOWN;
        const isBackdropUp = event.target === backdropRef.current;
        IS_BACKDROP_DOWN = false; // Reset for next click
        backdropProps?.onPointerUp?.(event);

        if (!strict && isBackdropDown && isBackdropUp) toggle(false); // Click on backdrop
      }}
      onClose={onClose}
      onKeyDown={(event: React.KeyboardEvent<HTMLDialogElement>) => {
        backdropProps?.onKeyDown?.(event);
        if (event.key === 'Escape') {
          event.preventDefault(); // Always prevent default, so we can add custom toggle tracking
          if (strict !== DISABLE_ESC) toggle(false);
        }
      }}
    >
      <div {...rest} className={classes} ref={ref}>
        {!!closeLabel && (
          <Button
            aria-label={closeLabel}
            className={styles.close}
            onClick={() => toggle(false)}
            variant="unset"
            icon={<Close />}
            tracking={false}
          />
        )}
        <div className={styles.content}>{children}</div>
      </div>
    </dialog>
  );
});

type ModalFooterProps = React.ComponentPropsWithRef<'div'>;

export const Modal = Object.assign(ModalComp, {
  Footer: forwardRef<HTMLDivElement, ModalFooterProps>(function Footer(
    { className, ...rest },
    ref
  ) {
    return (
      <div {...rest} className={clsx(styles.footer, className)} ref={ref} />
    );
  })
});
